import React, { useState } from 'react';
import {
  Box,
  Link,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { useLocale } from '../contexts/LocaleContext';
import { humanizeKey } from '../utils/displayLabels';
import { DataTableShell, EmptyState, InlineMessage, MetricCard, PageContainer, PageHeader, SectionCard, StatusChip } from '../components/ui';
import type { ReorderRecapEntry } from '../components/reorder/reorderApi';
import { REORDER_TABLE_HREF } from './dashboardShared/components/ReorderCard';

interface RecapNewBrand {
  brandId: string;
  brand: string;
  productCount: number;
  firstProductAt: string | null;
}
interface RecapNewProduct {
  id: string;
  name: string;
  brand: string;
  brandId: string;
  sku: string | null;
  productType: string | null;
  createdAt: string | null;
  newBrand: boolean;
  retail: number | null;
}
interface RecapPriceChange {
  productId: string;
  name: string;
  brand: string;
  brandId: string | null;
  sku: string | null;
  productType: string | null;
  oldPrice: number;
  newPrice: number;
  changePct: number | null;
  changedAt: string | null;
  source: 'catalog' | 'sync';
  integrationId: string | null;
  platform: string | null;
  /** Set when a midnight price batch (flag `midnight_price_activation`) wrote the price: `activated 00:00:41 PT · batch of 1,834 in 2m 10s`. */
  activation?: { batchId: string; label: string; lagMs: number | null } | null;
}
interface RecapPage<T> {
  rows: T[];
  total: number;
  page: number;
  limit: number;
}
interface ChangeRecap {
  since: string;
  until: string;
  windowDays: number;
  counts: { newBrands: number; newProducts: number; priceChanges: number };
  newBrands: RecapNewBrand[];
  newProducts: RecapPage<RecapNewProduct>;
  priceChanges: RecapPage<RecapPriceChange>;
  facets: { brands: Array<{ brandId: string; brand: string }>; productTypes: string[] };
  truncated: boolean;
  truncatedSections?: { newProducts: boolean; priceChanges: boolean };
  priceScope?: PriceScope;
  /** Predictive reorder (MOT-396): present only with `predictive_reorder` and Advanced analytics. */
  reorder?: ReorderRecapEntry;
}

type PriceScope = 'synced' | 'brands';

const WINDOWS = [7, 14, 30, 90];
const PAGE_SIZE = 25;

const productLink = (id: string): string => `/app/products?productId=${encodeURIComponent(id)}`;

const StoreChangeRecapPage: React.FC = () => {
  const { t: tx } = useAppTranslation();
  const { isEnabled, isLoading: flagsLoading } = useOrgFeatureFlags();
  const enabled = isEnabled('change_recap');
  const { formatCurrency, formatDate } = useLocale();
  const day = (iso: string | null): string => (iso ? formatDate(iso) : '');
  const money = (n: number | null | undefined): string => (typeof n === 'number' ? formatCurrency(n) : '');
  const [days, setDays] = useState(7);
  const [brandId, setBrandId] = useState('');
  const [productType, setProductType] = useState('');
  const [newPage, setNewPage] = useState(1);
  const [pricePage, setPricePage] = useState(1);
  const [priceScope, setPriceScope] = useState<PriceScope>('synced');

  const { data, isLoading, error } = useQuery<ChangeRecap>({
    queryKey: ['change-recap', days, brandId, productType, newPage, pricePage, priceScope],
    enabled,
    queryFn: async () => {
      const params = new URLSearchParams({
        since: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(),
        newPage: String(newPage),
        pricePage: String(pricePage),
        limit: String(PAGE_SIZE),
        scope: priceScope,
      });
      if (brandId) params.set('brandIds', brandId);
      if (productType) params.set('productTypes', productType);
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/change-recap?${params.toString()}`, { headers });
      if (!res.ok) throw new Error('Could not load what changed');
      return (await res.json()).data as ChangeRecap;
    },
  });

  // Filter options come from the server over the whole window, not just the visible pages.
  const brandEntries = (data?.facets?.brands ?? []).map((b) => [b.brandId, b.brand] as [string, string]);
  const typeOptions = data?.facets?.productTypes ?? [];

  const resetPages = () => {
    setNewPage(1);
    setPricePage(1);
  };

  if (!flagsLoading && !enabled) {
    return (
      <PageContainer>
        <EmptyState title="What changed is not available yet" description="This view is not turned on for your store." />
      </PageContainer>
    );
  }

  const pageCount = (p?: RecapPage<unknown>) => (p ? Math.max(1, Math.ceil(p.total / p.limit)) : 1);

  return (
    <PageContainer>
      <PageHeader
        title="What Changed"
        description="New brands and products you can sell, and price changes on the products you sync."
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField select size="small" label="Window" value={days} onChange={(e) => { setDays(Number(e.target.value)); resetPages(); }} sx={{ minWidth: 160 }}>
          {WINDOWS.map((d) => (
            <MenuItem key={d} value={d}>{`Last ${d} days`}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Brand" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} value={brandId} onChange={(e) => { setBrandId(e.target.value); resetPages(); }} sx={{ minWidth: 180 }}>
          <MenuItem value="">All brands</MenuItem>
          {brandEntries.map(([id, name]) => (
            <MenuItem key={id} value={id}>{name}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label={tx('changeRecapPage.scopeLabel')} value={priceScope} onChange={(e) => { setPriceScope(e.target.value as PriceScope); setPricePage(1); }} sx={{ minWidth: 240 }}>
          <MenuItem value="synced">{tx('changeRecapPage.scopeSynced')}</MenuItem>
          <MenuItem value="brands">{tx('changeRecapPage.scopeBrands')}</MenuItem>
        </TextField>
        <TextField select size="small" label="Product type" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} value={productType} onChange={(e) => { setProductType(e.target.value); resetPages(); }} sx={{ minWidth: 180 }}>
          <MenuItem value="">All types</MenuItem>
          {typeOptions.map((t) => (
            <MenuItem key={t} value={t}>{humanizeKey(t)}</MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && <InlineMessage severity="error">{(error as Error).message}</InlineMessage>}
      {data?.truncatedSections?.newProducts && (
        <InlineMessage severity="info">{tx('changeRecapPage.truncatedNew')}</InlineMessage>
      )}
      {data?.truncatedSections?.priceChanges && (
        <InlineMessage severity="info">
          {brandId || productType ? tx('changeRecapPage.truncatedPriceFiltered') : tx('changeRecapPage.truncatedPrice')}
        </InlineMessage>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <MetricCard label="New brands" value={data?.counts.newBrands ?? '-'} />
        <MetricCard label="New products" value={data?.counts.newProducts ?? '-'} />
        <MetricCard label="Price changes" value={data?.counts.priceChanges ?? '-'} />
      </Box>

      <Stack spacing={3}>
        <SectionCard dense title="New brands and products" subtitle={data ? `Since ${day(data.since)}` : undefined}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : !data || data.newProducts.total === 0 ? (
            <EmptyState size="compact" title="Nothing new in this window" description="New brands and products in your catalog scope show up here." />
          ) : (
            <>
              {data.newBrands.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ p: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  {data.newBrands.map((b) => (
                    <StatusChip key={b.brandId} tone="info" label={`New brand: ${b.brand} (${b.productCount})`} />
                  ))}
                </Stack>
              )}
              <DataTableShell>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell>Added</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.newProducts.rows.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Link component={RouterLink} to={productLink(p.id)} underline="hover">{p.name}</Link>
                          {p.sku && <Typography variant="caption" color="text.secondary" display="block">{p.sku}</Typography>}
                        </TableCell>
                        <TableCell>{p.brand} {p.newBrand && <StatusChip tone="info" label="New brand" />}</TableCell>
                        <TableCell>{p.productType ? humanizeKey(p.productType) : ''}</TableCell>
                        <TableCell align="right">{money(p.retail)}</TableCell>
                        <TableCell>{day(p.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataTableShell>
              {pageCount(data.newProducts) > 1 && (
                <Pagination sx={{ p: 2 }} count={pageCount(data.newProducts)} page={newPage} onChange={(_, v) => setNewPage(v)} />
              )}
            </>
          )}
        </SectionCard>

        <SectionCard dense title={tx(priceScope === 'brands' ? 'changeRecapPage.priceTitleBrands' : 'changeRecapPage.priceTitleSynced')}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : !data || data.priceChanges.total === 0 ? (
            <EmptyState size="compact" title="No price changes in this window" description={tx(priceScope === 'brands' ? 'changeRecapPage.priceEmptyBrands' : 'changeRecapPage.priceEmptySynced')} />
          ) : (
            <>
              <DataTableShell>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell align="right">Was</TableCell>
                      <TableCell align="right">Now</TableCell>
                      <TableCell align="right">Change</TableCell>
                      <TableCell>When</TableCell>
                      <TableCell>Pushed to</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.priceChanges.rows.map((p) => (
                      <TableRow key={p.productId} hover>
                        <TableCell>
                          <Link component={RouterLink} to={productLink(p.productId)} underline="hover">{p.name}</Link>
                          {p.sku && <Typography variant="caption" color="text.secondary" display="block">{p.sku}</Typography>}
                        </TableCell>
                        <TableCell>{p.brand}</TableCell>
                        <TableCell align="right">{money(p.oldPrice)}</TableCell>
                        <TableCell align="right">{money(p.newPrice)}</TableCell>
                        <TableCell align="right">
                          {p.changePct === null ? '' : <StatusChip tone={p.changePct < 0 ? 'success' : 'warning'} label={`${p.changePct > 0 ? '+' : ''}${p.changePct}%`} />}
                        </TableCell>
                        <TableCell>{day(p.changedAt)}</TableCell>
                        <TableCell>
                          {p.platform || (p.integrationId ? 'Integration' : '')}
                          {p.activation && (
                            <Typography variant="caption" color="text.secondary" display="block">{p.activation.label}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataTableShell>
              {pageCount(data.priceChanges) > 1 && (
                <Pagination sx={{ p: 2 }} count={pageCount(data.priceChanges)} page={pricePage} onChange={(_, v) => setPricePage(v)} />
              )}
            </>
          )}
        </SectionCard>

        {data?.reorder && (
          <SectionCard
            dense
            title={tx('changeRecapPage.reorder.title')}
            subtitle={tx('changeRecapPage.reorder.subtitle', { crossed: data.reorder.crossed, now: data.reorder.reorderNow, since: day(data.reorder.since) })}
            actions={
              <Link component={RouterLink} to={REORDER_TABLE_HREF} variant="body2">
                {tx('changeRecapPage.reorder.open')}
              </Link>
            }
          >
            {data.reorder.products.length === 0 ? (
              <EmptyState size="compact" title={tx('changeRecapPage.reorder.emptyTitle')} description={tx('changeRecapPage.reorder.emptyBody')} />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tx('changeRecapPage.reorder.product')}</TableCell>
                    <TableCell align="right">{tx('changeRecapPage.reorder.onHand')}</TableCell>
                    <TableCell align="right">{tx('changeRecapPage.reorder.reorderAt')}</TableCell>
                    <TableCell align="right">{tx('changeRecapPage.reorder.order')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.reorder.products.map((p) => (
                    <TableRow key={p.productId}>
                      <TableCell>
                        <Link component={RouterLink} to={productLink(p.productId)} variant="body2">{p.name}</Link>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[p.brand, p.sku].filter(Boolean).join(' · ')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{p.onHand == null ? '—' : p.onHand.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{p.reorderPoint.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{p.suggestedQty == null ? '—' : p.suggestedQty.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        )}
      </Stack>
    </PageContainer>
  );
};

export default StoreChangeRecapPage;
